<template>
  <main class="lesson-page">
    <template v-if="lesson">
      <header class="lesson-header">
        <div class="header-inner">
          <v-btn icon class="icon-action" aria-label="Zurück zum Modul" @click="$router.push(`/akademie/modules/${$route.params.id}`)">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="header-copy">
            <div class="header-eyebrow">Lektion {{ (lesson.order || 0) + 1 }}</div>
            <h1>{{ lesson.title }}</h1>
          </div>
          <span class="content-badge"><v-icon>{{ getContentIcon(lesson.contentType) }}</v-icon>{{ contentLabel(lesson.contentType) }}</span>
        </div>
      </header>

      <div class="lesson-content">
        <v-card v-if="lesson.introduction" dark flat class="introduction-card">
          <span><v-icon>mdi-information-outline</v-icon></span>
          <p>{{ lesson.introduction }}</p>
        </v-card>

        <section v-if="['video', 'audio', 'image'].includes(lesson.contentType)" class="media-surface">
          <SecuredMedia v-if="streamPath" ref="securedMedia" :src="streamPath" :media-type="lesson.contentType" eager />
        </section>

        <v-card v-if="lesson.contentType === 'text'" dark flat class="reader-card">
          <div class="reader-heading">
            <span><v-icon>mdi-text-box-outline</v-icon></span>
            <div>
              <div>Lesemodus</div>
              <h2>{{ lesson.title }}</h2>
            </div>
          </div>
          <div class="html-content" v-html="lesson.textContent"></div>
        </v-card>
      </div>

      <footer class="lesson-footer">
        <div class="footer-inner">
          <div class="completion-hint">
            <v-icon :color="canComplete ? '#7ee5a6' : '#a3a3a8'">{{ canComplete ? "mdi-check-circle-outline" : "mdi-clock-outline" }}</v-icon>
            <span>{{ canComplete ? "Du kannst diese Lektion abschliessen." : "Der Abschluss wird gleich freigeschaltet." }}</span>
          </div>
          <v-btn
            color="#009640"
            depressed
            class="complete-action"
            :disabled="!canComplete"
            :loading="completing"
            @click="completeLesson">
            <v-icon left>mdi-check-circle-outline</v-icon>
            Lektion abschliessen
          </v-btn>
        </div>
      </footer>
    </template>

    <div v-else class="loading-surface">
      <v-progress-circular indeterminate color="#58d388" size="48" width="4" />
    </div>
  </main>
</template>

<script>
import api from "@/api"
import SecuredMedia from "./SecuredMedia.vue"

export default {
  name: "LessonPlayer",
  components: { SecuredMedia },
  data() {
    return {
      lesson: null,
      moduleItem: null,
      streamPath: "",
      canComplete: false,
      completing: false,
      autoCompleteTimer: null,
    }
  },
  mounted() {
    this.loadLesson()
  },
  beforeDestroy() {
    if (this.autoCompleteTimer) clearTimeout(this.autoCompleteTimer)
  },
  methods: {
    async loadLesson() {
      try {
        const moduleItem = await api.akademie.fetchModule(this.$route.params.id)
        if (!moduleItem) return
        this.moduleItem = moduleItem
        if (moduleItem.lessons) {
          this.lesson = moduleItem.lessons.find((entry) => entry._id === this.$route.params.lessonId)
        }
        if (this.lesson && this.lesson.mediaFile) {
          this.streamPath = api.akademie.getLessonStreamPath(this.lesson._id)
        }
        if (this.lesson) {
          if (["text", "image"].includes(this.lesson.contentType)) {
            this.canComplete = true
          } else {
            this.autoCompleteTimer = setTimeout(() => (this.canComplete = true), 5000)
          }
        }
      } catch (error) {
        console.error("Fehler beim Laden der Lektion:", error)
      }
    },
    getContentIcon(type) {
      const icons = { video: "mdi-video-outline", audio: "mdi-music-note-outline", image: "mdi-image-outline", text: "mdi-text-box-outline" }
      return icons[type] || "mdi-file-outline"
    },
    contentLabel(type) {
      const labels = { video: "Video", audio: "Audio", image: "Bild", text: "Text" }
      return labels[type] || "Inhalt"
    },
    async completeLesson() {
      this.completing = true
      try {
        await api.akademie.completeLesson(this.$route.params.id, this.lesson._id)
        this.$router.push(`/akademie/modules/${this.$route.params.id}`)
      } catch (error) {
        console.error("Completion error", error)
      } finally {
        this.completing = false
      }
    },
  },
}
</script>

<style scoped>
.lesson-page {
  --surface: rgba(255, 255, 255, 0.035);
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding-bottom: 6.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.lesson-header {
  position: sticky;
  z-index: 5;
  top: 0;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: rgba(18, 18, 18, 0.92);
  backdrop-filter: blur(1rem);
  -webkit-backdrop-filter: blur(1rem);
}

.header-inner,
.footer-inner {
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
}

.header-inner {
  display: grid;
  grid-template-columns: 2.75rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.header-copy {
  min-width: 0;
}

.header-eyebrow,
.reader-heading > div > div {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.header-copy h1 {
  margin: 0.125rem 0 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 1.125rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-badge {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.75rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
  font-size: 0.75rem;
  font-weight: 750;
}

.content-badge .v-icon {
  color: currentColor;
  font-size: 1.125rem;
}

.lesson-content {
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: 1.5rem;
}

.introduction-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1rem !important;
  background: var(--surface) !important;
}

.introduction-card > span {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.6875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.introduction-card > span .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.introduction-card p {
  margin: 0;
  color: #d4d4d8;
  font-size: 1rem;
  line-height: 1.65;
}

.media-surface,
.reader-card {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.25rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1.25rem 3rem rgba(0, 0, 0, 0.24);
}

.media-surface ::v-deep .secured-media-content {
  width: 100%;
  max-height: 75vh;
  border-radius: 1.25rem;
}

.reader-card {
  padding: 1.5rem;
}

.reader-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.reader-heading > span {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.reader-heading > span .v-icon {
  color: currentColor;
}

.reader-heading h2 {
  margin: 0.125rem 0 0;
  color: #ffffff;
  font-size: 1.25rem;
}

.html-content {
  padding-top: 1.25rem;
  color: #e4e4e7;
  font-size: 1rem;
  line-height: 1.8;
}

.lesson-footer {
  position: fixed;
  z-index: 6;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0.75rem 1.5rem max(0.75rem, env(safe-area-inset-bottom));
  border-top: 1px solid var(--border);
  background: rgba(18, 18, 18, 0.94);
  backdrop-filter: blur(1rem);
  -webkit-backdrop-filter: blur(1rem);
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.completion-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #b8b8bc;
  font-size: 0.875rem;
}

.complete-action {
  min-height: 3rem !important;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.loading-surface {
  display: grid;
  min-height: 100vh;
  min-height: 100dvh;
  place-items: center;
}

@media (max-width: 37.4375rem) {
  .lesson-page {
    padding-bottom: 9rem;
  }

  .lesson-header,
  .lesson-content,
  .lesson-footer {
    padding-right: 0.75rem;
    padding-left: 0.75rem;
  }

  .content-badge {
    width: 2.75rem;
    padding: 0;
    justify-content: center;
  }

  .content-badge .v-icon {
    font-size: 1.25rem;
  }

  .lesson-content {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }

  .reader-card {
    padding: 1rem;
    border-radius: 1.0625rem !important;
  }

  .footer-inner {
    align-items: stretch;
    flex-direction: column;
    gap: 0.625rem;
  }

  .completion-hint {
    justify-content: center;
    text-align: center;
  }

  .complete-action {
    width: 100%;
  }
}
</style>
